import { useEffect, useState } from 'react';
import { Button } from '@coordio/ui';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';

export interface ExecuteWorkflowDialogProps {
  open: boolean;
  workflowName: string;
  /** When true, an empty input blocks execution (graph references {{input}}). */
  requiresInput: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: (input: string) => void;
}

/**
 * Collects the free-text runtime input before executing a workflow.
 * The value is sent as `parameters.input` and resolved into `{{input}}`
 * placeholders in agent-node message templates.
 */
export const ExecuteWorkflowDialog = ({
  open,
  workflowName,
  requiresInput,
  loading,
  onClose,
  onConfirm,
}: ExecuteWorkflowDialogProps) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setInput('');
      setError(null);
    }
  }, [open]);

  const handleConfirm = () => {
    if (requiresInput && !input.trim()) {
      setError('This workflow uses {{input}} — an input value is required.');
      return;
    }
    setError(null);
    onConfirm(input);
  };

  return (
    <CortexDialog open={open} onClose={loading ? undefined : onClose} title="Execute workflow" size="lg" data-testid="execute-workflow-dialog">
      <CortexDialogBody>
        <p className="text-sm text-muted-foreground mb-4">
          {requiresInput
            ? `"${workflowName}" expects a runtime input (resolved into {{input}} placeholders).`
            : `"${workflowName}" does not reference {{input}} — you can leave the input blank.`}
        </p>
        <div className="flex flex-col gap-1">
          <label htmlFor="execute-workflow-input" className="text-sm font-medium">
            {requiresInput ? 'Input (required)' : 'Input (optional)'}
          </label>
          <textarea
            id="execute-workflow-input"
            className={`w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring ${error ? 'border-red-500 focus:ring-red-500' : 'border-input'}`}
            autoFocus
            rows={3}
            placeholder="input → {{input}}"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (error) setError(null);
            }}
            disabled={loading}
          />
          {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
      </CortexDialogBody>
      <CortexDialogFooter>
        <Button variant="outline" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={loading}>
          {loading ? <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" /> : 'Execute'}
        </Button>
      </CortexDialogFooter>
    </CortexDialog>
  );
};
